import { signal, computed } from '@angular/core';
import { LoginOrganizationSite } from 'api/models';
import { CurrentSiteRole } from '@app/shared/models/authentication/session';

export interface AppState {
    currentOrganizationSite: LoginOrganizationSite | null;
    currentSiteRole: CurrentSiteRole | null;
    availableOrganizationSites: LoginOrganizationSite[];
    isLoading: boolean;
    error: string | null;
    lastUpdated: number;
    selectedLanguage: string;
    isLoggedIn: boolean;
    isTourPlanMode: boolean;
    isUploadMode: boolean;
    // Column visibility state - key is viewType_userRole, value is array of visible column keys
    columnVisibility: { [key: string]: string[] };
    // Location filter state - key is userRole, value is array of selected site keys
    selectedLocations: { [key: string]: string[] };
    // Show closed filter state - global boolean for all views
    showClosed: boolean;
    // Dialog state - track when table dialogs are open to prevent pagination conflicts
    isSharePermissionsDialogOpen: boolean;
}

const loadShowClosedFromStorage = (): boolean => {
    try {
        const stored = localStorage.getItem('showClosed');
        return stored !== null ? JSON.parse(stored) : true; // Default to true
    } catch {
        return true; // Default to true if parsing fails
    }
};

const initialState: AppState = {
    currentOrganizationSite: null,
    currentSiteRole: null,
    availableOrganizationSites: [],
    isLoading: false,
    error: null,
    lastUpdated: 0,
    selectedLanguage: 'de',
    isLoggedIn: false,
    isTourPlanMode: false,
    isUploadMode: false,
    columnVisibility: {},
    selectedLocations: {},
    showClosed: loadShowClosedFromStorage(),
    isSharePermissionsDialogOpen: false,
};

export const appState = signal<AppState>(initialState);

// Computed selectors
export const getCurrentOrganizationSite = computed(
    () => appState().currentOrganizationSite
);
export const getCurrentSiteRole = computed(() => appState().currentSiteRole);
export const getAvailableOrganizationSites = computed(
    () => appState().availableOrganizationSites
);
export const getIsLoading = computed(() => appState().isLoading);
export const getError = computed(() => appState().error);
export const getLastUpdated = computed(() => appState().lastUpdated);
export const getSelectedLanguage = computed(() => appState().selectedLanguage);
export const getIsLoggedIn = computed(() => appState().isLoggedIn);
export const getIsTourPlanMode = computed(() => appState().isTourPlanMode);
export const getIsUploadMode = computed(() => appState().isUploadMode);
export const getColumnVisibility = computed(() => appState().columnVisibility);
export const getSelectedLocations = computed(
    () => appState().selectedLocations
);
export const getIsSharePermissionsDialogOpen = computed(
    () => appState().isSharePermissionsDialogOpen
);

// Computed derived values
export const getCurrentOrganizationSiteKey = computed(
    () => appState().currentOrganizationSite?._key || null
);
export const getCurrentSiteName = computed(
    () => appState().currentSiteRole?.siteInfo?.fullSiteName || null
);
export const getCurrentRole = computed(
    () => appState().currentSiteRole?.role || null
);
export const getIsCurrentSiteAdmin = computed(() => {
    const siteRole = appState().currentSiteRole;
    return (
        siteRole?.role === 'SiteAdmin' || siteRole?.role === 'OrganizationAdmin'
    );
});

// Actions
export const setAppLoading = (loading: boolean) => {
    appState.update(state => ({
        ...state,
        isLoading: loading,
    }));
};

export const setAppError = (error: string | null) => {
    appState.update(state => ({
        ...state,
        error,
    }));
};

export const setCurrentOrganizationSite = (
    site: LoginOrganizationSite | null
) => {
    appState.update(state => ({
        ...state,
        currentOrganizationSite: site,
        lastUpdated: Date.now(),
    }));
};

export const setCurrentSiteRole = (siteRole: CurrentSiteRole | null) => {
    appState.update(state => ({
        ...state,
        currentSiteRole: siteRole,
        lastUpdated: Date.now(),
    }));
};

export const setAvailableOrganizationSites = (
    sites: LoginOrganizationSite[]
) => {
    appState.update(state => ({
        ...state,
        availableOrganizationSites: sites,
        lastUpdated: Date.now(),
    }));
};

export const setSelectedLanguage = (language: string) => {
    appState.update(state => ({
        ...state,
        selectedLanguage: language,
        lastUpdated: Date.now(),
    }));
};

export const setIsLoggedIn = (isLoggedIn: boolean) => {
    appState.update(state => ({
        ...state,
        isLoggedIn,
        lastUpdated: Date.now(),
    }));
};

export const setIsTourPlanMode = (isTourPlanMode: boolean) => {
    appState.update(state => ({
        ...state,
        isTourPlanMode,
        lastUpdated: Date.now(),
    }));

    // When entering tour plan mode, force selection to current site only
    if (isTourPlanMode) {
        const currentSiteKey = getCurrentOrganizationSiteKey();
        const currentSiteRole = getCurrentSiteRole();
        if (currentSiteKey && currentSiteRole?.role) {
            const userRole =
                currentSiteRole.role === 'carrier' ||
                currentSiteRole.role === 'remote_carrier'
                    ? 'carrier'
                    : currentSiteRole.role === 'consignor' ||
                        currentSiteRole.role === 'remote_consignor'
                      ? 'consignor'
                      : currentSiteRole.role === 'consignee' ||
                          currentSiteRole.role === 'remote_consignee'
                        ? 'consignee'
                        : null;
            if (userRole) {
                setSelectedLocations(userRole, [currentSiteKey]);
            }
        }
    }
};

export const toggleTourPlanMode = () => {
    const newTourPlanMode = !appState().isTourPlanMode;
    setIsTourPlanMode(newTourPlanMode);
};

export const setIsUploadMode = (isUploadMode: boolean) => {
    appState.update(state => ({
        ...state,
        isUploadMode,
        lastUpdated: Date.now(),
    }));
};

export const toggleUploadMode = () => {
    appState.update(state => ({
        ...state,
        isUploadMode: !state.isUploadMode,
        lastUpdated: Date.now(),
    }));
};

export const updateCurrentOrganizationSiteAndRole = (
    site: LoginOrganizationSite,
    siteRole: CurrentSiteRole
) => {
    appState.update(state => ({
        ...state,
        currentOrganizationSite: site,
        currentSiteRole: siteRole,
        // Clear all selected locations when switching sites - user needs to reselect
        selectedLocations: {},
        // Keep show closed filter when switching sites
        showClosed: state.showClosed,
        lastUpdated: Date.now(),
    }));

    // Set only the new site as selected for the current role
    if (siteRole?.role) {
        const userRole =
            siteRole.role === 'consignor' ||
            siteRole.role === 'remote_consignor'
                ? 'consignor'
                : siteRole.role === 'consignee' ||
                    siteRole.role === 'remote_consignee'
                  ? 'consignee'
                  : siteRole.role === 'carrier' ||
                      siteRole.role === 'remote_carrier'
                    ? 'carrier'
                    : null;

        if (userRole && site._key) {
            setSelectedLocations(userRole, [site._key]);

            // Trigger data refresh to get updated dataset for the new site
            const { triggerDataRefresh } = require('@app/stores/searchStore');
            triggerDataRefresh();
        }
    }
};

export const clearAppState = () => {
    appState.update(state => ({
        ...initialState,
        selectedLanguage: state.selectedLanguage, // Keep language preference
    }));
};

// Column visibility actions
export const setColumnVisibility = (
    viewKey: string,
    visibleColumns: string[]
) => {
    appState.update(state => ({
        ...state,
        columnVisibility: {
            ...state.columnVisibility,
            [viewKey]: visibleColumns,
        },
        lastUpdated: Date.now(),
    }));
};

export const getVisibleColumns = (
    viewType: string,
    userRole: string
): string[] => {
    const viewKey = `${viewType}_${userRole}`;
    return appState().columnVisibility[viewKey] || [];
};

export const hasColumnVisibilitySettings = (
    viewType: string,
    userRole: string
): boolean => {
    const viewKey = `${viewType}_${userRole}`;
    return viewKey in appState().columnVisibility;
};

export const clearColumnVisibility = (viewType: string, userRole: string) => {
    const viewKey = `${viewType}_${userRole}`;
    appState.update(state => {
        const newColumnVisibility = { ...state.columnVisibility };
        delete newColumnVisibility[viewKey];
        return {
            ...state,
            columnVisibility: newColumnVisibility,
            lastUpdated: Date.now(),
        };
    });
};

// Location filter actions
export const setSelectedLocations = (
    userRole: string,
    locationKeys: string[]
) => {
    // When in tour plan mode, ignore location changes and force current site only
    if (getIsTourPlanMode()) {
        const currentSiteKey = getCurrentOrganizationSiteKey();
        if (currentSiteKey) {
            locationKeys = [currentSiteKey];
        }
    }

    // If no locations are selected, automatically add the current organization site key
    let finalLocationKeys = locationKeys;
    if (locationKeys.length === 0) {
        const currentSiteKey = getCurrentOrganizationSiteKey();
        if (currentSiteKey) {
            finalLocationKeys = [currentSiteKey];
        }
    }

    appState.update(state => ({
        ...state,
        selectedLocations: {
            ...state.selectedLocations,
            [userRole]: finalLocationKeys,
        },
        lastUpdated: Date.now(),
    }));
};

export const getSelectedLocationKeys = (userRole: string): string[] => {
    return appState().selectedLocations[userRole] || [];
};

export const hasSelectedLocations = (userRole: string): boolean => {
    return (
        userRole in appState().selectedLocations &&
        appState().selectedLocations[userRole].length > 0
    );
};

export const addLocationToSelection = (
    userRole: string,
    locationKey: string
) => {
    const currentLocations = getSelectedLocationKeys(userRole);
    if (!currentLocations.includes(locationKey)) {
        setSelectedLocations(userRole, [...currentLocations, locationKey]);
    }
};

export const initializeSelectedLocations = (
    userRole: string,
    currentOrganizationSiteKey: string
) => {
    // Initialize with current location if no selection exists
    if (!hasSelectedLocations(userRole)) {
        setSelectedLocations(userRole, [currentOrganizationSiteKey]);
    } else {
    }
};

// Show closed filter actions
export const getShowClosed = (): boolean => {
    return appState().showClosed;
};

export const setShowClosed = (showClosed: boolean) => {
    // Save to localStorage
    try {
        localStorage.setItem('showClosed', JSON.stringify(showClosed));
    } catch (error) {
        console.warn('Failed to save showClosed to localStorage:', error);
    }

    appState.update(state => ({
        ...state,
        showClosed,
        lastUpdated: Date.now(),
    }));
};

export const toggleShowClosed = () => {
    const newShowClosed = !appState().showClosed;

    // Save to localStorage
    try {
        localStorage.setItem('showClosed', JSON.stringify(newShowClosed));
    } catch (error) {
        console.warn('Failed to save showClosed to localStorage:', error);
    }

    appState.update(state => ({
        ...state,
        showClosed: newShowClosed,
        lastUpdated: Date.now(),
    }));
};

// Dialog state actions
export const setSharePermissionsDialogOpen = (isOpen: boolean) => {
    appState.update(state => ({
        ...state,
        isSharePermissionsDialogOpen: isOpen,
        lastUpdated: Date.now(),
    }));
};
